import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

// Convert unpadded base64url → Uint8Array for PushManager.subscribe.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Detect browser / OS support plus two edge cases we must tell the user about:
 *   - iOS Safari: requires the app to be installed to Home Screen (iOS 16.4+).
 *   - Browsers with no ServiceWorker / PushManager at all.
 */
function detectEnvironment() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    // iOS legacy flag
    window.navigator.standalone === true;
  const hasServiceWorker = "serviceWorker" in navigator;
  const hasPushManager = "PushManager" in window;
  const hasNotification = "Notification" in window;
  const supported = hasServiceWorker && hasPushManager && hasNotification;
  const needsInstallForIOS = isIOS && !isStandalone;
  return { supported, isIOS, isStandalone, needsInstallForIOS };
}

/**
 * Push notification orchestration.
 * Exposes state + explicit enable/disable so the UI stays declarative.
 */
export default function usePushNotifications() {
  const env = detectEnvironment();
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Register the service worker once on mount if supported.
  useEffect(() => {
    if (!env.supported) return;
    navigator.serviceWorker.register("/sw.js").catch(() => {});
    // Check existing subscription
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(Boolean(sub));
      } catch (_) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enable = useCallback(async () => {
    setError("");
    if (!env.supported) {
      setError("Push notifications are not supported in this browser.");
      return;
    }
    if (env.needsInstallForIOS) {
      setError("Install ACCP to your home screen first (Share → Add to Home Screen), then enable notifications from inside the installed app.");
      return;
    }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setError("Permission denied. You can re-enable notifications in browser settings.");
        return;
      }
      const { data: keyResp } = await api.get("/push/vapid-public-key");
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(keyResp.key),
        });
      }
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
    } catch (e) {
      setError(e?.message || "Could not enable push notifications.");
    } finally {
      setBusy(false);
    }
  }, [env.needsInstallForIOS, env.supported]);

  const disable = useCallback(async () => {
    setError("");
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const json = sub.toJSON();
        await sub.unsubscribe().catch(() => {});
        await api.post("/push/unsubscribe", {
          endpoint: json.endpoint,
          keys: json.keys || { p256dh: "", auth: "" },
        }).catch(() => {});
      }
      setSubscribed(false);
    } catch (e) {
      setError(e?.message || "Could not disable notifications.");
    } finally {
      setBusy(false);
    }
  }, []);

  return { env, permission, subscribed, busy, error, enable, disable };
}
